import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import QAManagement from '../WidgetEditComponents/QAManagement';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function QAWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.qaLayout);
    
    // Upload States
    const [isUploadingLeftImage, setIsUploadingLeftImage] = useState(false);
    const [isUploadingRightImage, setIsUploadingRightImage] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);


    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 QAWidget initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.qaLayout;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 QAWidget: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 QAWidget: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 QAWidget REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-side-image': {
                height: 'auto',
                heightMobile: 'auto',
                showLeftImage: true,
                leftImageWidth: 35,
                leftImageHeight: 400,
                contentSpacing: 40,
                qaItemPadding: 20,
                qaItemSpacing: 0, // ✅ Kein Abstand zwischen Items
                qaItemBorderStyle: 'frameless', // ✅ Frameless (Connected List) als Default
                qaItemBorderRadius: 0
            },
            'full-width-minimal': {
                height: 'auto',
                heightMobile: 'auto',
                paddingTop: 0, // ✅ Kein Top-Padding für volle Breite
                paddingBottom: 0, // ✅ Kein Bottom-Padding für volle Breite
                paddingTopMobile: 0,
                paddingBottomMobile: 0,
                showLeftImage: false,
                contentSpacing: 0,
                qaItemPadding: 24, // ✅ Größeres Padding für bessere Symmetrie
                qaItemSpacing: 0, // ✅ Kein Abstand zwischen Items
                qaItemBorderStyle: 'frameless', // ✅ Frameless auch für minimal
                qaItemBorderRadius: 0,
                qaItemBackgroundColor: 'transparent',
                titleAlignment: 'center' // ✅ Titel zentriert für Symmetrie
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ QAWidget layout variations applied via batch update`);
        }
    }, [editData.qaLayout, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 QAWidget layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'qaLayout', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Q&A Items Management
    const addQAItem = () => {
        const currentItems = editData.qaItems || [];
        const newItem = {
            id: `qa${Date.now()}`,
            question: 'New question?',
            answer: 'New answer text.',
            isExpanded: false
        };
        const updatedItems = [...currentItems, newItem];
        handleInputChange({ target: { name: 'qaItems', value: updatedItems } });
    };

    const removeQAItem = (index) => {
        const currentItems = editData.qaItems || [];
        const updatedItems = currentItems.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'qaItems', value: updatedItems } });
    };

    const updateQAItem = (index, field, value) => {
        const currentItems = editData.qaItems || [];
        const updatedItems = [...currentItems];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        handleInputChange({ target: { name: 'qaItems', value: updatedItems } });
    };

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Nur Titel Header mit Show/Hide Toggle
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Q&A Title Header',
            type: 'input',
            placeholder: 'Enter Q&A title...',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        }
    ];

    // Color Configuration (ColorPickerSection-kompatibel)
    const colorConfig = [
        [
            { fieldName: 'titleColor', label: 'Title', defaultValue: '#2c3e50' },
            { fieldName: 'questionColor', label: 'Questions', defaultValue: '#2c3e50' },
            { fieldName: 'answerColor', label: 'Answers', defaultValue: '#666666' }
        ],
        [
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#ffffff' },
            { fieldName: 'qaItemBackgroundColor', label: 'Q&A Items', defaultValue: 'transparent' },
            { fieldName: 'qaItemBorderColor', label: 'Border/Underline', defaultValue: '#e0e0e0' }
        ],
        [
            { fieldName: 'iconColor', label: 'Icons', defaultValue: '#666666' }
        ]
    ];

    // Font Configuration (ResponsiveFontControls-kompatibel) - Mit Mobile Support
    const fontConfig = [
        {
            label: 'Title Font',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            mobileSizeField: 'titleSizeMobile',
            mobileWeightField: 'titleWeightMobile',
            defaultSize: '32',
            defaultWeight: '700',
            defaultMobileSize: '28',
            defaultMobileWeight: '700'
        },
        {
            label: 'Question Font',
            sizeField: 'questionSize',
            weightField: 'questionWeight',
            mobileSizeField: 'questionSizeMobile',
            mobileWeightField: 'questionWeightMobile',
            defaultSize: '18',
            defaultWeight: '600',
            defaultMobileSize: '16',
            defaultMobileWeight: '600'
        },
        {
            label: 'Answer Font',
            sizeField: 'answerSize',
            weightField: 'answerWeight',
            mobileSizeField: 'answerSizeMobile',
            mobileWeightField: 'answerWeightMobile',
            defaultSize: '16',
            defaultWeight: '400',
            defaultMobileSize: '14',
            defaultMobileWeight: '400'
        }
    ];

    // ✅ Layout Options jetzt über LayoutEngine.js verwaltet

    // ✅ REVOLUTIONARY LAYOUT SETTINGS - NUR Standard Properties (Widget-spezifische in Advanced Settings)
    const customLayoutProperties = [
        {
            section: 'Q&A Spacing',
            icon: 'fa-arrows-v',
            properties: [
                {
                    id: 'titleContentSpacing',
                    label: 'Title Content Spacing',
                    type: 'slider',
                    min: 0,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    defaultValue: 40,
                    icon: '↕️',
                    description: 'Spacing between title and Q&A content',
                    responsive: true // ✅ Ermöglicht Desktop/Mobile getrennte Einstellungen
                },
                {
                    id: 'qaItemPaddingVertical',
                    label: 'Q&A Item Padding Vertical',
                    type: 'slider',
                    min: 0,
                    max: 40,
                    step: 2,
                    unit: 'px',
                    defaultValue: 20,
                    icon: '↕️',
                    description: 'Vertical padding (top/bottom) of Q&A items',
                    responsive: true // ✅ Desktop/Mobile getrennte Einstellungen
                },
                {
                    id: 'qaItemPaddingHorizontal',
                    label: 'Q&A Item Padding Horizontal',
                    type: 'slider',
                    min: 0,
                    max: 60,
                    step: 2,
                    unit: 'px',
                    defaultValue: 20,
                    icon: '↔️',
                    description: 'Horizontal padding (left/right) of Q&A items',
                    responsive: true // ✅ Desktop/Mobile getrennte Einstellungen
                },
                {
                    id: 'imageMarginBottom',
                    label: 'Image Margin Bottom',
                    type: 'slider',
                    min: 0,
                    max: 60,
                    step: 5,
                    unit: 'px',
                    defaultValue: 20,
                    icon: '🖼️',
                    description: 'Bottom spacing below image',
                    responsive: true // ✅ Desktop/Mobile getrennte Einstellungen
                },
                {
                    id: 'titleMarginLeft',
                    label: 'Title Margin Left',
                    type: 'slider',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    defaultValue: 0,
                    icon: '📝',
                    description: 'Left margin of the title',
                    responsive: true // ✅ Desktop/Mobile getrennte Einstellungen
                },
                {
                    id: 'titleMarginRight',
                    label: 'Title Margin Right',
                    type: 'slider',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    defaultValue: 0,
                    icon: '📝',
                    description: 'Right margin of the title',
                    responsive: true // ✅ Desktop/Mobile getrennte Einstellungen
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Widget-spezifische Properties
    const advancedConfig = [
        // Image Settings
        {
            fieldName: 'showLeftImage',
            label: 'Show Left Image',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        
        // Content Settings
        {
            fieldName: 'qaItemSpacing',
            label: 'Item Spacing',
            type: 'slider',
            min: 0,
            max: 20,
            step: 2,
            unit: 'px',
            defaultValue: 0,
            icon: '/img/element.svg'
        },
        
        // Text Alignment Settings
        {
            fieldName: 'titleAlignment',
            label: 'Title Alignment',
            type: 'select', // ✅ Korrekt für AdvancedSettingsSection
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/text-fat.svg'
        },
        
        // Behavior Settings
        {
            fieldName: 'enableHoverEffects',
            label: 'Enable Hover Effects',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'qaItemBorderStyle',
            label: 'Item Border Style',
            type: 'select',
            options: [
                { value: 'none', label: '🚫 No Border' },
                { value: 'border', label: '⬜ Full Border (Individual Items)' },
                { value: 'frameless', label: '📄 Frameless (Connected List)' },
                { value: 'underline', label: '📏 Underline Only' }
            ],
            defaultValue: 'frameless',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'qaItemBorderWidth',
            label: 'Border/Underline Width',
            type: 'slider',
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            defaultValue: 1,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'qaItemBorderRadius',
            label: 'Q&A Item Border Radius',
            type: 'slider',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            defaultValue: 8,
            icon: '/img/element.svg'
        },

        // Display Group - Image Border Radius moved to ImageContainer
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="qaWidget"
                    selectedLayout={editData.qaLayout || 'classic-side-image'}
                    onLayoutChange={handleLayoutChange}
                />
                
                
                {/* ✅ IMAGE CONTAINER */}
            <ImageContainer
                label="Left Image"
                fieldName="leftImage"
                currentImage={editData.leftImage}
                onUpload={uploadImage}
                isUploading={isUploadingLeftImage}
                setIsUploading={setIsUploadingLeftImage}
                showToggle={true}
                toggleLabel="Show Left Image"
                toggleFieldName="showLeftImage"
                toggleChecked={editData.showLeftImage || false}
                onToggleChange={handleInputChange}
                controls={[
                    {
                        type: 'slider',
                        label: 'Image Width',
                        fieldName: 'leftImageWidth',
                        defaultValue: 35,
                        min: 20,
                        max: 50,
                        step: 5,
                        unit: '%',
                        description: 'Width of the left image'
                    },
                    {
                        type: 'slider',
                        label: 'Image Height',
                        fieldName: 'leftImageHeight',
                        defaultValue: 400,
                        min: 200,
                        max: 800,
                        step: 50,
                        unit: 'px',
                        description: 'Height of the left image'
                    },
                    {
                        type: 'slider',
                        label: 'Border Radius',
                        fieldName: 'leftImageBorderRadius',
                        defaultValue: 0,
                        min: 0,
                        max: 50,
                        step: 2,
                        unit: 'px',
                        description: 'Roundness of image corners'
                    },
                    {
                        type: 'select',
                        label: 'Image Fit',
                        fieldName: 'leftImageFit',
                        defaultValue: 'cover',
                        options: [
                            { value: 'cover', label: 'Cover - Fill container' },
                            { value: 'contain', label: 'Contain - Fit inside' },
                            { value: 'fill', label: 'Fill - Stretch to fit' }
                        ]
                    },
                    {
                        type: 'slider',
                        label: 'Mobile Height',
                        fieldName: 'mobileImageHeight',
                        defaultValue: 200,
                        min: 100,
                        max: 400,
                        step: 10,
                        unit: 'px',
                        description: 'Height on mobile devices'
                    },
                    {
                        type: 'select',
                        label: 'Mobile Position',
                        fieldName: 'mobileImagePosition',
                        defaultValue: 'top',
                        options: [
                            { value: 'top', label: '📱 Show on Top' },
                            { value: 'hidden', label: '🚫 Hide on Mobile' }
                        ]
                    }
                ]}
                editData={editData}
                handleInputChange={handleInputChange}
                theme="default"
            />

            {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Nur Titel Header mit Show/Hide Toggle */}
            <TextSettingsSection 
                textConfig={textConfig}
                editData={editData}
                handleInputChange={handleInputChange}
                showFontSelector={true}
                showTextEffects={true}
                showButtonEffects={false}
            />

            {/* Q&A Items Management */}
            <QAManagement 
                qaItems={editData.qaItems || []}
                addQAItem={addQAItem}
                removeQAItem={removeQAItem}
                updateQAItem={updateQAItem}
                editData={editData}
            />

            {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
            <RevolutionaryLayoutSettings 
                editData={editData}
                handleInputChange={handleInputChange}
                customProperties={customLayoutProperties}
            />

            {/* 🔧 ADVANCED SETTINGS - Nur noch Widget-spezifische Properties */}
            <AdvancedSettingsSection 
                advancedConfig={advancedConfig}
                editData={editData}
                handleInputChange={handleInputChange}
                handleSliderChange={handleSliderChange}
                defaultExpanded={false}
            />

            {/* More Settings (Colors & Fonts only) */}
            <MoreSettingsSection 
                colors={colors}
                colorConfig={colorConfig}
                fontConfig={fontConfig}
                layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                editData={editData}
                handleInputChange={handleInputChange}
            />

            {/* 💾 SAVE AS TEMPLATE BUTTON */}
            <div className="save-template-section">
                <button 
                    className="save-template-btn"
                    onClick={() => setShowSaveTemplatePopup(true)}
                    title="Save this widget configuration as a reusable template"
                >
                    <i className="fas fa-save"></i>
                    Save as Template
                </button>
            </div>

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />
            </div>
        </div>
    );
}

export default QAWidgetEdit;
